import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LogOut } from 'lucide-react';

interface ProfileOrbMenuProps {
  isDarkMode: boolean;
  name?: string | null;
  email?: string | null;
  picture?: string | null;
  onLogout: () => void;
}

function getDisplayName(name?: string | null, email?: string | null) {
  const trimmedName = String(name || '').trim();
  const trimmedEmail = String(email || '').trim();
  if (trimmedName && trimmedEmail && trimmedName.toLowerCase() === trimmedEmail.toLowerCase() && trimmedEmail === 'tim@mlst.ai') {
    return 'Tim Scarfe';
  }
  return trimmedName || trimmedEmail || 'User';
}

function getInitials(name?: string | null, email?: string | null) {
  const displayName = getDisplayName(name, email);
  const parts = displayName.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase();
}

const ProfileOrbMenu: React.FC<ProfileOrbMenuProps> = ({
  isDarkMode,
  name,
  email,
  picture,
  onLogout,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const displayName = useMemo(() => getDisplayName(name, email), [name, email]);
  const initials = useMemo(() => getInitials(name, email), [name, email]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border transition-colors ${
          isDarkMode
            ? 'border-gray-600 bg-gray-700 hover:border-primary-400'
            : 'border-gray-300 bg-white hover:border-primary-500'
        }`}
        title={displayName}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {picture ? (
          <img
            src={picture}
            alt={displayName}
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="text-xs font-semibold">{initials}</span>
        )}
      </button>

      {open && (
        <div
          className={`absolute right-0 top-12 z-50 w-64 rounded-2xl border shadow-2xl ${
            isDarkMode
              ? 'border-gray-700 bg-gray-800 text-gray-100'
              : 'border-gray-200 bg-white text-gray-900'
          }`}
          role="menu"
        >
          <div className={`border-b px-4 py-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`flex h-12 w-12 items-center justify-center overflow-hidden rounded-full ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}>
                {picture ? (
                  <img
                    src={picture}
                    alt={displayName}
                    className="h-full w-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="text-sm font-semibold">{initials}</span>
                )}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{displayName}</div>
                {email && (
                  <div className={`truncate text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {email}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${
                isDarkMode
                  ? 'text-red-300 hover:bg-red-500/10 hover:text-red-200'
                  : 'text-red-600 hover:bg-red-50 hover:text-red-700'
              }`}
              role="menuitem"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileOrbMenu;
